import { Card, Code, Group, SimpleGrid, Stack, Table, Text, Title } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import type { DataObject, UploadSession } from '@/api/objects';
import { StatusBadge } from '@/components/StatusBadge';
import { useFormat } from '@/components/useFormat';
import { useKindLabel } from '@/features/objects/useKindLabel';
import { brand } from '@/theme/theme';
import { InternalStateChip } from './InternalStateChip';
import { InternalSwitch } from './InternalSwitch';
import { UriCell } from './UriCell';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Stack gap={2}>
      <Text fz={12} fw={700} tt="uppercase" c={brand.textMuted} style={{ letterSpacing: '0.06em' }}>
        {label}
      </Text>
      <Text fz={14} component="div">
        {children}
      </Text>
    </Stack>
  );
}

/** Every field of one object and its sessions (staff object page). */
export function ObjectInternals({ object, sessions }: { object: DataObject; sessions: UploadSession[] }) {
  const { t } = useTranslation('projects');
  const { bytes, dateTime } = useFormat();
  const kindLabel = useKindLabel();
  const dash = (v: unknown) => (v === null || v === undefined || v === '' ? '—' : String(v));

  return (
    <Stack gap="lg">
      <Card>
        <Group justify="space-between" mb="md">
          <Group gap="sm">
            <Title order={3}>{kindLabel(object.kind)}</Title>
            <InternalStateChip state={object.state ?? ''} />
            <StatusBadge status={object.client_status} scope="object" />
          </Group>
          <Text ff="monospace" fz={12} c={brand.textMuted}>
            {object.public_id}
          </Text>
        </Group>
        <SimpleGrid cols={{ base: 2, md: 4 }} spacing="md">
          <Field label="origin">{object.origin}</Field>
          <Field label="name">{dash(object.name)}</Field>
          <Field label="declared_name">{dash(object.declared_name)}</Field>
          <Field label="notes">{dash(object.notes)}</Field>
          <Field label="needs_preprocessing">{String(object.needs_preprocessing)}</Field>
          <Field label="file_count">{object.file_count}</Field>
          <Field label="total_bytes">{bytes(object.total_bytes ?? 0)}</Field>
          <Field label="raw_bytes">{bytes(object.raw_bytes ?? 0)}</Field>
          <Field label="checksum">{dash(object.checksum)}</Field>
          <Field label="crs">{dash(object.crs)}</Field>
          <Field label="bounds">{object.bounds ? JSON.stringify(object.bounds) : '—'}</Field>
          <Field label="resolution">{dash(object.resolution)}</Field>
          <Field label="is_deliverable">{String(object.is_deliverable)}</Field>
          <Field label="replaces">{dash(object.replaces)}</Field>
          <Field label="created_at">{dateTime(object.created_at)}</Field>
          <Field label="state_changed_at">{dateTime(object.state_changed_at)}</Field>
          <Field label="uploaded_by">{object.uploaded_by?.full_name ?? '—'}</Field>
          <Field label="deleted">{object.deleted ? dateTime(object.deleted_at) : 'false'}</Field>
          <Field label="raw_deleted">{object.raw_deleted ? dateTime(object.raw_deleted_at) : 'false'}</Field>
          <Field label="rejection_reason">{object.rejection_reason ? JSON.stringify(object.rejection_reason) : '—'}</Field>
        </SimpleGrid>
        <Stack gap="xs" mt="md">
          <InternalSwitch object={object} />
          <Field label="raw_uri">
            <UriCell uri={object.raw_uri} full />
          </Field>
          <Field label="processed_uri">
            <UriCell uri={object.processed_uri} full />
          </Field>
        </Stack>
        {!!object.capture_metadata && Object.keys(object.capture_metadata as object).length > 0 && (
          <Code block mt="md" fz={11}>
            {JSON.stringify(object.capture_metadata, null, 2)}
          </Code>
        )}
      </Card>

      <Card p={0}>
        <Title order={3} px="md" py="sm">
          {t('staff.sessions')}
        </Title>
        {sessions.length === 0 ? (
          <Text px="md" pb="md" fz={14} c={brand.textMuted}>
            {t('staff.noSessions')}
          </Text>
        ) : (
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>id</Table.Th>
                <Table.Th>state</Table.Th>
                <Table.Th>file</Table.Th>
                <Table.Th>bytes</Table.Th>
                <Table.Th>expires</Table.Th>
                <Table.Th>failure</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {sessions.map((s) => (
                <Table.Tr key={s.public_id}>
                  <Table.Td>
                    <Text ff="monospace" fz={12}>
                      {s.public_id}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <InternalStateChip state={s.state ?? ''} />
                  </Table.Td>
                  <Table.Td>{s.target_name}</Table.Td>
                  <Table.Td>
                    {bytes(s.bytes_uploaded ?? 0)} / {bytes(s.declared_bytes)}
                  </Table.Td>
                  <Table.Td>{dateTime(s.expires_at)}</Table.Td>
                  <Table.Td>{s.failure_reason || '—'}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Card>
    </Stack>
  );
}
